import { ReactElement, useMemo } from 'react'
import { getPolygonNormal, SelectionBorder } from '@scene'
import { useTheme } from '@mui/material'
import { ImmutableVector3, sceneConfig } from '@modugen/ui-toolkit'
import { ThreeEvent } from '@react-three/fiber'
import { useTriggerContextMenu } from '@ui/hooks'
import { useGuidFocusTransparencyOpacity } from '@editorHooks'
import SimpleShape from '../SimpleShape'

interface Props {
  points: ImmutableVector3[]
  thickness?: number
  onClick?: (event: ThreeEvent<MouseEvent>) => void
  highlighted?: boolean
  nonSelectable?: boolean
  wallGuid: string
  guid: string
}

const SimpleStiffeningMeshElement = ({
  points,
  thickness,
  onClick,
  highlighted = false,
  nonSelectable = false,
  wallGuid,
  guid,
}: Props): ReactElement => {
  const { scenePalette } = useTheme()
  const focusOpacity = useGuidFocusTransparencyOpacity(guid, 0.4)

  const { openContextMenuViaEvent } = useTriggerContextMenu()

  const userData = useMemo(() => {
    const clipPlaneNormal = getPolygonNormal(points)
    const clipPlanePoint = points[0]

    return {
      contextMenuActions: {
        hide: {
          guid: wallGuid,
          label: 'Verstecken (Wand)',
        },
        properties: {
          guid,
          label: 'Eigenschaften (Aussteifungssegment)',
        },
        propertiesWall: {
          guid: wallGuid,
          label: 'Eigenschaften (Wand)',
        },
        'clip-plane': {
          normal: clipPlaneNormal,
          point: clipPlanePoint,
        },
      },
      contextType: 'stiffening-mesh',
    }
  }, [guid, points, wallGuid])

  return (
    <>
      <SimpleShape
        name={guid}
        points={points}
        color={highlighted ? scenePalette.selection : scenePalette.elements3d.stiffening}
        thickness={thickness}
        opacity={focusOpacity}
        meshMaterialProps={{ transparent: focusOpacity < 1 }}
        outlines
        outlinesColor={scenePalette.outlines}
        onClick={onClick}
        onPointerUp={openContextMenuViaEvent}
        cursor={onClick && 'pointer'}
        layers={nonSelectable ? sceneConfig.R3FNonSelectableObjectLayer : undefined}
        userData={userData}
      />

      <SelectionBorder points={points} selected={highlighted} />
    </>
  )
}

export default SimpleStiffeningMeshElement
